// Global Instructions Rule Applied!

import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch } from '../../../state_manager/store.ts'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import {
  setTopics,
  setActiveFilters,
  setActiveSorting
} from '../../../state_manager/state_update_actions/distribution_lists/index.ts'
import { FilterValue, SortConfig } from '../../../state_manager/interfaces/distribution_lists.ts'
import { fetchTopicsFiltered, TopicsPaginatedResponse } from '../services/distributionListsService.ts'

export const DEFAULT_TOPICS_SORTING: SortConfig[] = [{ key: 'comms_topics__label', direction: 'asc' }]

export const useTopicsData = () => {
  const dispatch = useDispatch<AppDispatch>()

  const [topicsDataLoading, setTopicsDataLoading] = useState(false)

  const topicsData = useSelector((state: any) => state.distributionLists?.data ?? [])
  const activeFilters = useSelector((state: any) => state.distributionLists?.activeFilters ?? [])
  const activeSorting = useSelector(
    (state: any) => state.distributionLists?.activeSorting ?? DEFAULT_TOPICS_SORTING
  )

  const handleFetchTopics = useCallback(
    async (sorting: SortConfig[] = DEFAULT_TOPICS_SORTING, filters: FilterValue[] = []) => {
      try {
        setTopicsDataLoading(true)
        const currentToken = getAccessToken(store.getState().auth)
        const response: TopicsPaginatedResponse = await fetchTopicsFiltered(
          sorting && sorting.length > 0 ? sorting : DEFAULT_TOPICS_SORTING,
          filters ?? [],
          1,
          null,
          currentToken
        )
        dispatch(setTopics({ response }))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching topics:', e)
        }
      } finally {
        setTopicsDataLoading(false)
      }
    },
    [dispatch]
  )

  const handleSetFilters = useCallback(
    (filters: FilterValue[]) => {
      dispatch(setActiveFilters({ filters }))
    },
    [dispatch]
  )

  const handleSetSorting = useCallback(
    (sorting: SortConfig[]) => {
      dispatch(setActiveSorting({ sorting }))
    },
    [dispatch]
  )

  // Initial fetch + refetch whenever filters / sorting change. The screen
  // reads through the data RPC (not the cached globalConfigs copy) so a
  // post-write refetch is correct without a backend restart.
  useEffect(() => {
    const currentToken = getAccessToken(store.getState().auth)
    if (currentToken) {
      handleFetchTopics(activeSorting, activeFilters)
    }
  }, [activeFilters, activeSorting, handleFetchTopics])

  return {
    topicsData,
    topicsDataLoading,
    activeFilters,
    activeSorting,
    handleFetchTopics,
    refetch: () => handleFetchTopics(activeSorting, activeFilters),
    handleSetFilters,
    handleSetSorting
  }
}
